/**
 * Vad varje abonnemang kan kosta oss per månad.
 *
 * Modellen utgår från planernas riktiga gränser (annonser, skanningar,
 * bildrensningar, sökningar, djupvärderingar) och prissätter varje del med de
 * uppmätta priserna i ai-costs.ts. Inga nya siffror hittas på här: allt hämtas
 * från AI_OPERATIONS, MODEL_RATES, lagrings- och trafikpriserna.
 */
import {
  AI_OPERATIONS,
  EXTERNAL_RATES,
  EGRESS_SEK_PER_GB,
  STORAGE_SEK_PER_GB_MONTH,
  sekForCall,
  type AiOperation,
} from "./ai-costs";
import { CLEAN_IMAGE_DAILY_LIMIT, CLEAN_IMAGE_MONTHLY_LIMIT } from "./clean-image";
import { PHOTO_SCAN_MAX_IMAGES, PHOTO_SCAN_PER_DAY } from "./photo-scan";
import { PLANS, type PlanDefinition, type PlanId } from "./plans";
import { SCAN_MAX_SECONDS } from "./video-scan";

/** Snittmånad i beräkningen. Dygnsgränser räknas upp med den här faktorn. */
export const DAYS_PER_MONTH = 30;
/** Antal veckor per månad när veckogränsen är den som styr. */
export const WEEKS_PER_MONTH = 30 / 7;

/** Videoskanningar per dygn – samma tak som kvotvisningen i plans.functions.ts. */
export const VIDEO_SCANS_PER_DAY: Record<PlanId, number> = {
  free: 0,
  plus: 0,
  pro: 10,
  expert: 10,
};

/**
 * Antaganden vi måste göra där gränsen är "obegränsat inom rimlig användning"
 * eller där kostnaden beror på hur mycket data som laddas upp.
 */
export const COST_ASSUMPTIONS = {
  /** Annonser per månad när planen saknar tak (rimlig användning). */
  unlimitedAdsPerMonth: 300,
  /** Omskrivningar per månad när planen saknar tak. */
  unlimitedRewritesPerMonth: 200,
  /** Djupvärderingar per månad när planen saknar tak. */
  unlimitedDeepValuationsPerMonth: 40,
  /** Storlek på en uppladdad annonsbild efter komprimering. */
  imageBytes: 700 * 1024,
  /** Storlek på en video på annonsen. */
  adVideoBytes: 25 * 1024 * 1024,
  /** Andel annonser med video (bland planer som har video). */
  adVideoShare: 0.3,
  /** Hur många månader en annons ligger uppe och tar lagringsplats. */
  storageMonths: 2,
  /** Hur många gånger annonsen syns i en lista eller ett sökresultat. */
  viewsPerAd: 40,
  /** Storlek på miniatyren som listvyn hämtar. */
  thumbBytes: 60 * 1024,
  /** Andel av visningarna som blir en öppnad annons med stora bilder. */
  detailViewShare: 0.15,
  /** Antal stora bilder som laddas när annonsen öppnas. */
  imagesPerDetailView: 3,
  /** Råfilm som laddas upp per videoskanning. */
  scanVideoBytes: 45 * 1024 * 1024,
  /** Bilder som sparas ur filmen per videoskanning. */
  scanFramesPerRun: 12,
  /** Marknadssökningar hos Perplexity per djupvärdering. */
  marketSearchesPerValuation: 3,
  /** Omvända bildsökningar (Google Lens) per djupvärdering. */
  lensSearchesPerValuation: 2,
  /** Vision-anrop per djupvärdering. */
  visionCallsPerValuation: 2,
  /** Hur många delar en film på max-längd delas upp i. */
  videoPartsPerScan: Math.ceil(SCAN_MAX_SECONDS / 30),
} as const;

export type CostRow = {
  id: string;
  label: string;
  /** Vad som händer, i klartext. */
  description: string;
  /** Antal gånger per månad vid fullt utnyttjande. */
  perMonth: number;
  /** Styckpris i kronor. */
  unitSek: number;
  /** Summa per månad i kronor. */
  totalSek: number;
  /** Vilken gräns i planen som sätter taket. */
  limitSource: string;
  /** Delsteg som styckpriset är summan av. */
  children?: { label: string; unitSek: number; note: string }[];
};

export type PlanCost = {
  plan: PlanDefinition;
  rows: CostRow[];
  /** Total kostnad per månad när allt utnyttjas. */
  maxSek: number;
  /** Intäkt per månad vid månadsbetalning. */
  revenueMonthSek: number;
  /** Intäkt per månad vid årsbetalning (årspriset delat på 12). */
  revenueYearSek: number;
  /** Annonser per månad som ligger till grund för lagring och trafik. */
  adsPerMonth: number;
};

function op(id: string): AiOperation {
  const found = AI_OPERATIONS.find((o) => o.id === id);
  if (!found) throw new Error(`Okänd AI-operation: ${id}`);
  return found;
}

/** Kostnad i kronor för ett anrop av en namngiven AI-operation. */
export function opSek(id: string): number {
  const o = op(id);
  return sekForCall(o.model, o.inputTokens, o.outputTokens);
}

/** Hur många annonser planen tillåter per månad, och vilken gräns som styr. */
export function adsPerMonthFor(plan: PlanDefinition): { count: number; source: string } {
  const { perMonth, perWeek, perDay } = plan.limits;
  const candidates: { count: number; source: string }[] = [];
  if (perMonth !== null) candidates.push({ count: perMonth, source: `${perMonth} per månad` });
  if (perWeek !== null)
    candidates.push({ count: Math.round(perWeek * WEEKS_PER_MONTH), source: `${perWeek} per vecka` });
  if (perDay !== null)
    candidates.push({ count: perDay * DAYS_PER_MONTH, source: `${perDay} per dygn` });
  if (candidates.length === 0)
    return {
      count: COST_ASSUMPTIONS.unlimitedAdsPerMonth,
      source: "obegränsat – antagande om rimlig användning",
    };
  // Den lägsta gränsen är den som faktiskt stoppar användaren.
  return candidates.reduce((a, b) => (b.count < a.count ? b : a));
}

const GB = 1024 ** 3;

/** Externt styckepris plus eventuella tokens, för en tjänst i EXTERNAL_RATES. */
function externalSek(model: string, inputTokens = 0, outputTokens = 0): number {
  const rate = EXTERNAL_RATES[model];
  if (!rate) return 0;
  const tokens = rate.tokenRate
    ? (inputTokens / 1_000_000) * rate.tokenRate.inputPerMillionSek +
      (outputTokens / 1_000_000) * rate.tokenRate.outputPerMillionSek
    : 0;
  return rate.sekPerCall + tokens;
}

/**
 * Delstegen i en djupvärdering, med styckpris per steg. Antalen och
 * tokennivåerna kommer från loggade körningar i ai_calls.
 */
export function deepValuationSteps(): { label: string; unitSek: number; note: string }[] {
  const A = COST_ASSUMPTIONS;
  const perSearch = externalSek("perplexity/sonar-pro", 502, 683);
  return [
    { label: "Identifiering", unitSek: opSek("deep-valuation-identify"), note: "1 anrop" },
    {
      label: "Verifiering av identifiering",
      unitSek: opSek("deep-valuation-identify-verdict"),
      note: "1 anrop",
    },
    { label: "Följdfrågor", unitSek: opSek("deep-valuation-questions"), note: "1 anrop" },
    { label: "Sammanvägning och värde", unitSek: opSek("deep-valuation"), note: "1 anrop" },
    {
      label: "Marknadssökning (Perplexity)",
      unitSek: perSearch * A.marketSearchesPerValuation,
      note: `${A.marketSearchesPerValuation} sökningar á ${perSearch.toFixed(2).replace(".", ",")} kr`,
    },
    {
      label: "Omvänd bildsökning (Google Lens)",
      unitSek: externalSek("serpapi/google-lens") * A.lensSearchesPerValuation,
      note: `${A.lensSearchesPerValuation} sökningar`,
    },
    {
      label: "Vision (webbmatchning)",
      unitSek: externalSek("google/vision-web-detection") * A.visionCallsPerValuation,
      note: `${A.visionCallsPerValuation} anrop`,
    },
  ];
}

/** Alla kostnadsrader för en plan vid fullt utnyttjande. */
export function planCost(planId: PlanId): PlanCost {
  const plan = PLANS[planId];
  const l = plan.limits;
  const A = COST_ASSUMPTIONS;
  const ads = adsPerMonthFor(plan);
  const rows: CostRow[] = [];

  const push = (r: Omit<CostRow, "totalSek">) =>
    rows.push({ ...r, totalSek: r.perMonth * r.unitSek });

  // 1. Annonser med AI-text
  push({
    id: "ad",
    label: "Skapa annons med AI",
    description: "Bildgranskning, rubrik, text, kategori och prisförslag i ett anrop.",
    perMonth: ads.count,
    unitSek: opSek("ad"),
    limitSource: ads.source,
  });

  // 2. Bildrensningar
  const clean = CLEAN_IMAGE_MONTHLY_LIMIT[planId];
  if (clean > 0) {
    push({
      id: "clean_image",
      label: "Rensa bakgrund på bild",
      description: "Bildmodellen genererar en ny bild med studiobakgrund.",
      perMonth: clean,
      unitSek: opSek("clean_image"),
      limitSource: `${clean} per månad (max ${CLEAN_IMAGE_DAILY_LIMIT[planId]} per dygn)`,
    });
  }

  // 3. Smarta sökningar
  const searches = l.aiSearchPerMonth;
  push({
    id: "search",
    label: "Smart sökning",
    description: "Tolkar fritext till sökord, kategori och prisintervall.",
    perMonth: searches,
    unitSek: opSek("search"),
    limitSource: `${l.aiSearchPerMonth} per månad (max ${l.aiSearchPerDay} per dygn)`,
  });

  // 4. Omskrivningar av annonstext
  const rewrites =
    l.aiRewritesPerMonth === null ? A.unlimitedRewritesPerMonth : l.aiRewritesPerMonth;
  if (rewrites > 0) {
    push({
      id: "rewrite",
      label: "Omskrivning av annonstext",
      description: "Skriver om rubrik och beskrivning på en befintlig annons.",
      perMonth: rewrites,
      unitSek: opSek("rewrite"),
      limitSource:
        l.aiRewritesPerMonth === null
          ? "obegränsat – antagande om rimlig användning"
          : `${l.aiRewritesPerMonth} per månad`,
    });
  }

  // 5. Fotoskanning
  const photoRuns = PHOTO_SCAN_PER_DAY[planId] * DAYS_PER_MONTH;
  if (photoRuns > 0) {
    push({
      id: "photo_scan",
      label: "Fotoskanning",
      description: `Upp till ${PHOTO_SCAN_MAX_IMAGES} bilder grupperas per pryl i ett anrop.`,
      perMonth: photoRuns,
      unitSek: opSek("photo_scan"),
      limitSource: `${PHOTO_SCAN_PER_DAY[planId]} per dygn`,
    });
  }

  // 6. Videoskanning: flera delanrop plus extraanrop per skanning
  const videoRuns = VIDEO_SCANS_PER_DAY[planId] * DAYS_PER_MONTH;
  if (videoRuns > 0) {
    const perRun = opSek("video_scan_part") * A.videoPartsPerScan + opSek("video_scan_extra");
    push({
      id: "video_scan",
      label: "Videoskanning",
      description: `Film på upp till ${SCAN_MAX_SECONDS} s analyseras i ${A.videoPartsPerScan} delar plus komplettering.`,
      perMonth: videoRuns,
      unitSek: perRun,
      limitSource: `${VIDEO_SCANS_PER_DAY[planId]} per dygn`,
    });
  }

  // 7. Djupvärdering (frågor + sammanvägning + marknadssökning)
  const deep =
    l.deepValuationsPerMonth === null
      ? A.unlimitedDeepValuationsPerMonth
      : l.deepValuationsPerMonth;
  if (deep > 0) {
    const steps = deepValuationSteps();
    push({
      id: "deep_valuation",
      label: "Djupvärdering",
      description:
        "Identifiering, verifiering, följdfrågor, sammanvägning, marknadssökningar och omvänd bildsökning.",
      perMonth: deep,
      unitSek: steps.reduce((sum, step) => sum + step.unitSek, 0),
      limitSource:
        l.deepValuationsPerMonth === null
          ? "obegränsat – antagande om rimlig användning"
          : `${l.deepValuationsPerMonth} per månad`,
      children: steps,
    });
  }

  // 8. Lagring: annonsbilder, annonsvideo och sparade bildrutor ur skanningar
  const adImageBytes = ads.count * l.maxImages * A.imageBytes;
  const adVideoBytes = l.video ? ads.count * A.adVideoShare * A.adVideoBytes : 0;
  const scanBytes =
    videoRuns * (A.scanVideoBytes + A.scanFramesPerRun * A.imageBytes) +
    photoRuns * PHOTO_SCAN_MAX_IMAGES * A.imageBytes;
  const storedGb = (adImageBytes + adVideoBytes + scanBytes) / GB;
  push({
    id: "storage",
    label: "Lagring",
    description: `Bilder, video och skanningsmaterial ligger kvar i ${A.storageMonths} månader.`,
    perMonth: Number((storedGb * A.storageMonths).toFixed(2)),
    unitSek: STORAGE_SEK_PER_GB_MONTH,
    limitSource: `${l.maxImages} bilder per annons${l.video ? " + video" : ""}`,
  });

  // 9. Trafik ut: besökare hämtar bilderna
  const detailViews = A.viewsPerAd * A.detailViewShare;
  const egressGb =
    (ads.count *
      (A.viewsPerAd * A.thumbBytes + detailViews * A.imagesPerDetailView * A.imageBytes)) /
    GB;
  push({
    id: "egress",
    label: "Datatrafik ut",
    description: `${A.viewsPerAd} listvisningar med miniatyr per annons och ${Math.round(detailViews)} öppnade annonser med ${A.imagesPerDetailView} stora bilder.`,
    perMonth: Number(egressGb.toFixed(2)),
    unitSek: EGRESS_SEK_PER_GB,
    limitSource: "följer antalet annonser",
  });

  const maxSek = rows.reduce((sum, r) => sum + r.totalSek, 0);

  return {
    plan,
    rows,
    maxSek,
    revenueMonthSek: plan.priceSek,
    revenueYearSek: plan.priceYearSek / 12,
    adsPerMonth: ads.count,
  };
}

/** Marginal i kronor och procent vid en viss användningsgrad (0–1). */
export function marginFor(cost: PlanCost, usage: number, billing: "month" | "year" = "month") {
  const revenue = billing === "year" ? cost.revenueYearSek : cost.revenueMonthSek;
  const spend = cost.maxSek * usage;
  return {
    revenue,
    spend,
    marginSek: revenue - spend,
    marginPct: revenue > 0 ? ((revenue - spend) / revenue) * 100 : null,
  };
}

/** Vid vilken användningsgrad planen går back. Null när priset är 0. */
export function breakEvenUsage(cost: PlanCost): number | null {
  if (cost.revenueMonthSek <= 0 || cost.maxSek <= 0) return null;
  return cost.revenueMonthSek / cost.maxSek;
}
